import { Button, FormControl, Stack } from '@mui/material';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import T, { bool, func } from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FormElements } from 'web-components';
import { updateMessagesMachineIrisV3 } from '../../../../../../redux/machines_v2/actions';
import { StyledContent, StyledContentGrid, StyledHandlers, StyledNewDialog } from '../elements';
import { getFormikError, getFormikHelperText, isLoading, validateBlankSpace } from '../../../../../../helpers/utils';
import { MessageStatusSchema } from '../../../../../../attrs/formValidation';
import { getStatusMachine } from '../../../../../../redux/machines/selectors';
import StatusCard from './StatusCard/StatusCard';

const labelAmend = 'machines.machine_details';

const StatusTypes = [{ type: 'critical' }, { type: 'warning' }, { type: 'maintenance' }];

const EditMessagesModal = ({ open, machineId, handleClose, data }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loadingState } = useSelector(getStatusMachine);

  const isSubmitting = isLoading(loadingState.status);

  const errorToastProps = {
    variant: 'error',
    autoHideDuration: 3000
  };

  const submitForm = (values, formikHelpers) => {
    const payload = {
      area_name: values?.area_name?.value.toString(),
      alarm_class_name: values?.critical?.value.toString(),
      warning_class_name: values?.warning?.value.toString(),
      maintenance_class_name: values?.maintenance?.value.toString()
    };

    try {
      dispatch(updateMessagesMachineIrisV3(machineId, payload));
      handleClose(true);
      formikHelpers.resetForm();
    } catch (error) {
      if (error.response && error.response.status === 500) {
        enqueueSnackbar(t('machines.generic_error'), errorToastProps);
      }
    }
  };

  const actionBar = (handleSubmit, errors, resetForm, dirty) => (
    <StyledHandlers>
      <Button
        color="secondary"
        onClick={() => {
          resetForm();
          handleClose();
        }}
      >
        {t('customers.form.cancel')}
      </Button>
      <Button
        variant="contained"
        data-testid="status-modal-submit"
        style={{ padding: '1rem 2.5rem' }}
        disabled={!dirty || Object.keys(errors).length !== 0 || isSubmitting}
        onClick={() => handleSubmit()}
        onMouseDown={event => {
          event.preventDefault();
        }}
      >
        {t(`${labelAmend}.handle_button`)}
      </Button>
    </StyledHandlers>
  );

  return (
    <Formik
      initialValues={{
        area_name: {
          value: data?.area_name || ''
        },
        critical: {
          value: data?.alarm_class_name || ''
        },
        warning: {
          value: data?.warning_class_name || ''
        },
        maintenance: {
          value: data?.maintenance_class_name || ''
        }
      }}
      validationSchema={MessageStatusSchema}
      onSubmit={(values, formikHelpers) => submitForm(values, formikHelpers)}
      enableReinitialize
    >
      {({ errors, handleBlur, handleChange, touched, values, handleSubmit, resetForm, dirty }) => {
        const getHelperText = getFormikHelperText({ errors, t });
        const getError = getFormikError({ errors, touched });
        return (
          <StyledNewDialog
            open={open}
            handleClose={(event, reason) => {
              if (reason && reason === 'backdropClick') return;
              handleClose();
            }}
            title={t(`${labelAmend}.messages.edit_modal.title`)}
            subTitle={t(`${labelAmend}.messages.edit_modal.subtitle`)}
            maxWidth="630px"
            maxHeight="672px"
            displayActionBar="true"
            actionBar={actionBar(handleSubmit, errors, resetForm, dirty)}
            shouldCloseOnOverlayClick={false}
            content={
              <StyledContent>
                <FormControl sx={{ width: '100%' }}>
                  <Stack direction="column" gap={4}>
                    <StyledContentGrid>
                      <FormElements.TextField
                        fullWidth
                        name="area_name"
                        id="area_name"
                        label={t(`${labelAmend}.messages.edit_modal.form.area_name`)}
                        margin="normal"
                        helperText={t(`${labelAmend}.messages.edit_modal.form.area_name_helper_text`)}
                        onBlur={handleBlur}
                        onChange={event => {
                          const { name, value } = event.target;
                          handleChange({
                            target: { name, value: { value: validateBlankSpace(value) } }
                          });
                        }}
                        value={values.area_name.value}
                      />
                    </StyledContentGrid>
                    {StatusTypes.map(statusType => (
                      <StatusCard
                        getError={getError}
                        getHelperText={getHelperText}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        key={`machine-status-card-${statusType.type}`}
                        statusType={statusType}
                        values={values}
                      />
                    ))}
                  </Stack>
                </FormControl>
              </StyledContent>
            }
          />
        );
      }}
    </Formik>
  );
};

EditMessagesModal.propTypes = {
  data: T.shape({
    alarm_class_name: T.string.isRequired,
    area_name: T.string,
    created_at: T.arrayOf(T.shape({})),
    created_by: T.arrayOf(T.shape({})),
    last_updated_by: T.arrayOf(T.shape({})),
    maintenance_class_name: T.string.isRequired,
    updated_at: T.string.isRequired,
    warning_class_name: T.string.isRequired
  }),
  machineId: T.string,
  open: bool.isRequired,
  handleClose: func.isRequired
};

EditMessagesModal.defaultProps = {
  data: null,
  machineId: null
};

export default EditMessagesModal;
